import { InformationCircle } from "@repo/icons";
import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { formatWithZonedDate, ServicePackageEnum } from "@repo/lib";
import { graphql } from "@repo/graphql-types/gql";
import { UserQueryDocument } from "@repo/graphql-types/graphql";
import {
  MessageDrawerRoot,
  MessageDrawerTitle,
  MessageDrawerBody,
  MessageDrawerActions,
  MessageDrawerActionButton,
} from "@/components/message-drawer";
import { trackEvent } from "@/lib/tracking";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";

const AceitarPacoteServicosMutation = graphql(/* GraphQL */ `
  mutation AceitarPacoteServicos($input: AceitarPacoteServicosInput!) {
    aceitarPacoteServicos(input: $input) {
      aceitarPacoteServicosOutput {
        codPacoteServicos
        codUsuario
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

export const AssistProAcceptanceConfirmDrawer = (): JSX.Element => {
  const { user } = useRouteContext({ strict: false });
  const router = useRouter();
  const { action } = useSearch({
    from: "/settings/assist/assist-pro-acceptance/",
  });

  const { mutate, isPending } = useGraphQLMutationWithErrorHandler(
    AceitarPacoteServicosMutation,
  );
  const invalidateUserQuery = useInvalidateQuery(UserQueryDocument);

  const proPackage = user.pacotesServicos.find(
    (servicePkg) => servicePkg.codPacoteServicos === ServicePackageEnum.Pro.valueOf(),
  );

  const endTrialDate = proPackage?.dataFimTrial
    ? formatWithZonedDate(proPackage.dataFimTrial, "d 'de' MMMM")
    : "";

  const handleClose = (open: boolean): void => {
    if (!open) {
      void router.navigate({
        search: {},
        replace: true,
      });
    }
  };

  const handleConfirm = (): void => {
    const codUsuarioPacoteServicos = proPackage?.codUsuarioPacoteServicos;
    if (!codUsuarioPacoteServicos) return;

    mutate(
      { input: { codUsuarioPacoteServicos } },
      {
        onSuccess: () => {
          trackEvent("Botão Confirmar Permanência Assist Pro Drawer Clicado");
          invalidateUserQuery();
          void router.navigate({
            search: { action: "show-success-assist-pro-drawer" },
            replace: true,
          });
        },
      },
    );
  };

  const handleDecideLater = (): void => {
    trackEvent("Botão Decidir Depois Assist Pro Drawer Clicado");
    void router.navigate({
      search: {},
      replace: true,
    });
  };

  return (
    <MessageDrawerRoot
      icon={InformationCircle}
      open={action === "open-confirm-assist-pro-drawer"}
      setOpen={handleClose}
      variant="secondary"
    >
      <MessageDrawerTitle>
        Quer permanecer com o Assist Pro depois do seu período gratuito?
      </MessageDrawerTitle>
      <MessageDrawerBody>
        <span>
          Confirmando agora, você continua no Assist Pro <b>{endTrialDate}.</b>
          <br />
          Vamos te avisar quando estiver perto e nenhuma cobrança será feita antes disso.
        </span>
      </MessageDrawerBody>
      <MessageDrawerActions>
        <MessageDrawerActionButton onClick={handleConfirm} loading={isPending}>
          Permanecer no Assist Pro
        </MessageDrawerActionButton>
        <MessageDrawerActionButton secondary onClick={handleDecideLater}>
          Quero decidir depois
        </MessageDrawerActionButton>
      </MessageDrawerActions>
    </MessageDrawerRoot>
  );
};
